import React, { useRef, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import CheckIcon from "@mui/icons-material/Check";
import RestoreIcon from "@mui/icons-material/Restore";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTheme, alpha } from "@mui/material";
import { 
    PlanStep, 
    getStepCurrentText, 
    isStepDeleted, 
    isStepModified 
} from "../hooks/usePlanManagement";
import ReactMarkdown from 'react-markdown'

interface PlanStepsProps {
    planSteps: PlanStep[];
    editingStepIndex: number | null;
    editText: string;
    canEdit: boolean;
    commandPrefix: string;
    onEditTextChange: (text: string) => void;
    onStartEditing: (index: number) => void;
    onSaveEdit: () => void;
    onCancelEdit: () => void;
    onToggleStep: (index: number) => void;
    onRevertStep: (index: number) => void;
    onResetAll: () => void;
    onAddStep: (afterIndex: number) => void;
    onEditKeyDown: (e: React.KeyboardEvent) => void;
}

export function PlanSteps({
    planSteps,
    editingStepIndex,
    editText,
    canEdit,
    commandPrefix,
    onEditTextChange,
    onStartEditing,
    onSaveEdit,
    onCancelEdit,
    onToggleStep,
    onRevertStep,
    onResetAll,
    onAddStep,
    onEditKeyDown,
}: PlanStepsProps) {
    const theme = useTheme();
    const editInputRef = useRef<HTMLInputElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [needsExpansion, setNeedsExpansion] = useState(false);
    const maxHeightVh = 30; // 30% of viewport height

    // Focus edit input when editing starts
    React.useEffect(() => {
        if (editingStepIndex !== null) {
            editInputRef.current?.focus();
        }
    }, [editingStepIndex]);

    // Check if content overflows the max height (only when not in edit mode)
    useEffect(() => {
        if (!canEdit && contentRef.current) {
            const checkOverflow = () => {
                const maxHeight = (window.innerHeight * maxHeightVh) / 100;
                const contentHeight = contentRef.current?.scrollHeight || 0;
                setNeedsExpansion(contentHeight > maxHeight);
            };
            
            // Check on mount and when content changes
            checkOverflow();
            
            // Also check on window resize
            window.addEventListener('resize', checkOverflow);
            return () => window.removeEventListener('resize', checkOverflow);
        } else {
            // In edit mode, always show full content
            setNeedsExpansion(false);
            setIsExpanded(false);
        }
    }, [canEdit, planSteps]);

    // Render connector line between steps with add button on hover
    const renderStepConnector = (afterIndex: number) => {
        if (!canEdit) return null;
        
        return (
            <Box
                key={`connector-${afterIndex}`}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    height: '32px',
                    my: 0.5,
                    '&:hover .add-step-btn': {
                        opacity: 1,
                        transform: 'scale(1)',
                    },
                    '&:hover .connector-line': {
                        bgcolor: alpha(theme.palette.primary.main, 0.4),
                    },
                }}
            >
                {/* Vertical line */}
                <Box
                    className="connector-line"
                    sx={{
                        position: 'absolute',
                        width: '2px',
                        height: '100%',
                        bgcolor: alpha(theme.palette.divider, 0.4),
                        borderRadius: '1px',
                        transition: 'background-color 0.2s ease',
                    }}
                />
                {/* Add button - hidden by default, shown on hover */}
                <IconButton
                    className="add-step-btn"
                    size="small"
                    onClick={() => onAddStep(afterIndex)}
                    sx={{
                        opacity: 0,
                        transform: 'scale(0.8)',
                        transition: 'all 0.2s ease',
                        bgcolor: theme.palette.background.paper,
                        border: `2px solid ${theme.palette.primary.main}`,
                        color: theme.palette.primary.main,
                        width: 24,
                        height: 24,
                        zIndex: 1,
                        '&:hover': {
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                        },
                    }}
                >
                    <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        );
    };

    // Render a single plan step with action buttons
    const renderPlanStep = (step: PlanStep, index: number) => {
        const isEditing = editingStepIndex === index && canEdit;
        const currentText = getStepCurrentText(step);
        const isDeleted = isStepDeleted(step);
        const isModified = isStepModified(step);
        const hasEdits = step.changes.some(c => c.type === 'edit');
        const isNewStep = step.originalText === "";
        
        // Determine background and border colors based on state
        const getBgColor = () => {
            if (isEditing) return alpha(theme.palette.primary.light, 0.15);
            if (isDeleted) return alpha(theme.palette.action.disabled, 0.1);
            if (isNewStep) return alpha(theme.palette.success.light, 0.12);
            if (hasEdits) return alpha(theme.palette.warning.light, 0.1);
            return alpha(theme.palette.background.paper, 0.5);
        };
        
        const getBorderColor = () => {
            if (isEditing) return theme.palette.primary.main;
            if (isDeleted) return theme.palette.grey[400];
            if (isNewStep) return theme.palette.success.main;
            if (hasEdits) return theme.palette.warning.main;
            return theme.palette.divider;
        };
        
        return (
            <Box
                key={index}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: canEdit ? 1.5 : 2,
                    borderRadius: canEdit ? '8px' : '12px',
                    bgcolor: getBgColor(),
                    border: `1px solid ${alpha(
                        getBorderColor(),
                        isEditing || hasEdits || isNewStep ? 0.5 : 0.3
                    )}`,
                    transition: 'all 0.2s ease',
                    opacity: isDeleted ? 0.6 : 1,
                    minWidth: 0, // Prevents flex container from expanding beyond parent
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: isDeleted 
                            ? theme.palette.grey[500] 
                            : isNewStep 
                                ? theme.palette.success.main 
                                : theme.palette.primary.main,
                        minWidth: '24px',
                        fontSize: canEdit ? '0.875rem' : '0.95rem',
                    }}
                >
                    {index + 1}.
                </Typography>
                
                {isEditing ? (
                    // Inline edit mode
                    <InputBase
                        inputRef={editInputRef}
                        value={editText}
                        onChange={(e) => onEditTextChange(e.target.value)}
                        onKeyDown={onEditKeyDown}
                        onBlur={onSaveEdit}
                        fullWidth
                        multiline
                        sx={{
                            flexGrow: 1,
                            fontSize: '0.875rem',
                            minWidth: 0, // Prevents flex item from overflowing
                            '& .MuiInputBase-input': {
                                p: 0.5,
                                borderRadius: '4px',
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                            },
                        }}
                        autoFocus
                    />
                ) : (
                    // Display mode
                    <Typography
                        variant="body2"
                        component="div"
                        sx={{
                            flexGrow: 1,
                            color: isDeleted ? theme.palette.grey[500] : theme.palette.text.primary,
                            textDecoration: isDeleted ? 'line-through' : 'none',
                            fontStyle: hasEdits && !isDeleted ? 'italic' : 'normal',
                            fontSize: canEdit ? '0.875rem' : '0.95rem',
                            lineHeight: canEdit ? 1.5 : 1.6,
                            '& p': { m: 0, display: 'inline' }, // keeps it inline with the step number
                            '& strong': { fontWeight: 700 },
                            '& code': { 
                                bgcolor: alpha(theme.palette.text.primary, 0.1), 
                                px: 0.6, py: 0.2, 
                                borderRadius: 1, 
                                fontFamily: 'monospace',
                                fontSize: '0.85em'
                            },
                            '& a': {
                                color: theme.palette.success.dark,
                                textDecoration: 'underline',
                                fontWeight: 600,
                                '&:hover': {
                                    color: theme.palette.success.main,
                                },
                            },
                        }}
                    >
                        <ReactMarkdown>{currentText}</ReactMarkdown>
                    </Typography>
                )}
                
                {isEditing ? (
                    // Edit mode buttons
                    <>
                        <IconButton
                            size="small"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onSaveEdit();
                            }}
                            title="Save"
                            sx={{
                                color: theme.palette.success.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                },
                            }}
                        >
                            <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onCancelEdit();
                            }}
                            title="Cancel"
                            sx={{
                                color: theme.palette.grey[500],
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        {/* Revert to original - only show if step has changes */}
                        {isModified && (
                            <IconButton
                                size="small"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onRevertStep(index);
                                }}
                                title="Revert to original"
                                sx={{
                                    color: theme.palette.warning.main,
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                    },
                                }}
                            >
                                <RestoreIcon fontSize="small" />
                            </IconButton>
                        )}
                    </>
                ) : canEdit ? (
                    // Normal mode buttons (only when editing is allowed)
                    <>
                        <IconButton
                            size="small"
                            onClick={() => onStartEditing(index)}
                            disabled={isDeleted}
                            title="Edit"
                            sx={{
                                color: theme.palette.primary.main,
                                opacity: isDeleted ? 0.3 : 0.7,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                },
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => onToggleStep(index)}
                            title={isDeleted ? "Restore" : "Delete"}
                            sx={{
                                color: isDeleted ? theme.palette.success.main : theme.palette.error.main,
                                opacity: 0.7,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: isDeleted
                                        ? alpha(theme.palette.success.main, 0.1)
                                        : alpha(theme.palette.error.main, 0.1),
                                },
                            }}
                        >
                            {isDeleted ? <UndoIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                        </IconButton>
                    </>
                ) : null}
                </Box>
            </Box>
        );
    };

    if (planSteps.length === 0) return null;

    return (
        <>
            {/* Plan Steps */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '16px 16px 16px 4px',
                    bgcolor: alpha(theme.palette.success.light, 0.15),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box
                    ref={contentRef}
                    sx={{
                        p: 2,
                        maxHeight: !canEdit && needsExpansion && !isExpanded ? `${maxHeightVh}vh` : 'none',
                        overflow: !canEdit && needsExpansion && !isExpanded ? 'hidden' : 'visible',
                        transition: 'max-height 0.3s ease',
                        position: 'relative',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            color: theme.palette.text.primary,
                            lineHeight: 1.7,
                            mb: 2,
                        }}
                    >
                        Here's my plan to help you:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: canEdit ? 0 : 1.5 }}>
                        {planSteps.map((step, stepIndex) => (
                            <React.Fragment key={stepIndex}>
                                {renderPlanStep(step, stepIndex)}
                                {/* Add connector after each step except the last (only when editing) */}
                                {canEdit && stepIndex < planSteps.length - 1 && renderStepConnector(stepIndex)}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
                
                {/* Show All / Show Less Button */}
                {!canEdit && needsExpansion && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 1,
                            px: 2,
                            bgcolor: alpha(theme.palette.success.light, 0.15),
                            borderTop: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        }}
                    >
                        <Button
                            size="small"
                            onClick={() => setIsExpanded(!isExpanded)}
                            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{
                                textTransform: 'none',
                                color: theme.palette.success.dark,
                                fontWeight: 600,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.success.main, 0.15),
                                },
                            }}
                        >
                            {isExpanded ? 'Show Less' : 'Show All'}
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Pending changes display (editing phase only) */}
            {canEdit && commandPrefix && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.grey[500], 0.1),
                        border: `1px dashed ${alpha(theme.palette.grey[500], 0.3)}`,
                    }}
                >
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.palette.grey[500],
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                display: 'block',
                                mb: 0.5,
                            }}
                        >
                            Pending Changes
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.grey[600],
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {commandPrefix}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={onResetAll}
                        title="Reset all changes"
                        sx={{
                            color: theme.palette.grey[500],
                            '&:hover': {
                                color: theme.palette.error.main,
                            },
                        }}
                    >
                        <UndoIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
        </>
    );
}
